import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import ScrollAnimation from '../components/ScrollAnimation';
import { companyInfo } from '../data/mock';
import { Target, Eye, Award, Users } from 'lucide-react';

const About = () => {
  const stats = [
    { number: "35+", label: "Projects" },
    { number: "25", label: "Happy Clients" },
    { number: "5", label: "Years of AI Experience" },
    { number: "100%", label: "Client Satisfaction" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <ScrollAnimation>
            <h1 className="text-5xl md:text-7xl font-league-spartan font-extralight text-gray-900 mb-6 tracking-tight leading-none">
              About Devsurf
            </h1>
          </ScrollAnimation>
          <ScrollAnimation delay={200}>
            <p className="text-xl text-gray-600 font-league-spartan font-light mb-12 max-w-3xl mx-auto leading-relaxed">
              We're a team of AI innovators, engineers, and visionaries dedicated to transforming businesses through intelligent automation.
            </p>
          </ScrollAnimation>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <ScrollAnimation key={index} delay={index * 100}>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-league-spartan font-light text-primary mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-league-spartan font-medium">
                    {stat.label}
                  </div>
                </div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <ScrollAnimation>
              <div>
                <h2 className="text-4xl md:text-5xl font-league-spartan font-light text-gray-900 mb-8 tracking-tight">
                  Our Mission
                </h2>
                <p className="text-lg text-gray-700 font-league-spartan leading-relaxed mb-8">
                  {companyInfo.mission}
                </p>
                <h3 className="text-2xl font-league-spartan font-light text-gray-900 mb-6">Our Vision</h3>
                <p className="text-lg text-gray-700 font-league-spartan leading-relaxed">
                  {companyInfo.vision}
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={200}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {companyInfo.values.map((value, index) => (
                  <Card key={index} className="bg-white border border-gray-200 hover:border-primary/20 hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                        {getValueIcon(index)}
                      </div>
                      <h4 className="font-league-spartan font-medium text-gray-900 mb-3">
                        {value.title}
                      </h4>
                      <p className="text-gray-600 font-league-spartan text-sm leading-relaxed">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>

      {/* Team Section removed intentionally as per request */}

      {/* Company Story */}
      <section className="py-24 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <ScrollAnimation className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-league-spartan font-light text-gray-900 mb-6 tracking-tight">
              Our Story
            </h2>
          </ScrollAnimation>
          
          <ScrollAnimation delay={200}>
            <div className="prose prose-lg max-w-none text-gray-700 font-league-spartan leading-relaxed space-y-6">
              <p>
                Founded in 2019, Devsurf emerged from a simple yet powerful vision: to make artificial intelligence accessible and impactful for businesses of all sizes. Our founders, having witnessed the transformative potential of AI in various industries, recognized the need for a specialized partner that could bridge the gap between cutting-edge research and practical business applications.
              </p>
              <p>
                Starting with a small team of passionate AI engineers and researchers, we began by solving complex problems for startups and enterprises across different sectors. From healthcare to finance, e-commerce to education, we've had the privilege of working with diverse clients, each presenting unique challenges that have shaped our expertise and approach.
              </p>
              <p>
                Today, Devsurf stands as a trusted AI partner, having delivered over 50 successful projects and maintained a 100% client satisfaction rate. Our commitment to innovation, quality, and ethical AI practices continues to drive us forward as we help shape the future of intelligent business solutions.
              </p>
              <p>
                We believe that the best AI solutions are born from a deep understanding of both technology and business needs. This philosophy guides everything we do, from initial consultation to final deployment and beyond.
              </p>
            </div>
          </ScrollAnimation>
        </div>
      </section>
    </div>
  );
};

// Helper function to get value icons
const getValueIcon = (index) => {
  const icons = [
    <Target className="h-6 w-6 text-primary" />,
    <Users className="h-6 w-6 text-primary" />,
    <Award className="h-6 w-6 text-primary" />,
    <Eye className="h-6 w-6 text-primary" />
  ];
  return icons[index] || icons[0];
};

export default About;